import classNames from "classnames";
import DOMPurify from "dompurify";
import React, { useCallback, type PropsWithChildren, type ReactNode } from "react";
import { LoadingStatus } from "../../../Blocks/ProvetOnlineBooking/components/shared/loadingStatus";
import { Spinner } from "../../../Blocks/ProvetOnlineBooking/components/shared/spinner";
import { ProvetDialogStep } from "../../../Blocks/ProvetOnlineBooking/types";
import { Svg, SvgSize } from "../../Assets/Svg/React";
import { Button, ButtonColor, ButtonSize } from "../../Buttons/React";
import Collapsible from "./collapsible";

interface CollapsibleStepProps {
  id?: string;
  buttonId?: string;
  containerClass?: string;
  contentClass?: string;
  title?: string;
  subtitle?: string;
  stepCompleteMessageText?: string | ReactNode;
  stepCompleteMessageTextContainsHtml?: boolean;
  icon?: string;
  isOpen: boolean;
  isComplete: boolean;
  isPreselected?: boolean;
  isLoading?: boolean;
  step: ProvetDialogStep;
  selectedValue?: string;
  editButtonText?: string;
  confirmButtonText?: string;
  changeButtonText?: string;
  nextButtonActivated?: boolean;
  nextButtonText?: string;
  onEdit?: (id: string | undefined, step: ProvetDialogStep) => void;
  onConfirm?: (id: string | undefined, step: ProvetDialogStep) => void;
  onComplete?: (id: string | undefined, step: ProvetDialogStep) => void;
}

export const CollapsibleStep = ({
  id,
  title,
  subtitle,
  stepCompleteMessageText,
  stepCompleteMessageTextContainsHtml = false,
  icon,
  nextButtonActivated,
  nextButtonText,
  step,
  selectedValue,
  editButtonText,
  confirmButtonText,
  changeButtonText,
  isLoading = false,
  children,
  isComplete,
  isPreselected = false,
  isOpen,
  onEdit,
  onConfirm,
  onComplete
}: PropsWithChildren<CollapsibleStepProps>) => {
  const editStep = useCallback(() => {
    if (!!onEdit) {
      onEdit(id, step);
    }
  }, [id, step, onEdit]);

  const confirmStep = useCallback(() => {
    if (!!onConfirm) {
      onConfirm(id, step);
    }
  }, [id, step, onConfirm]);

  const completeStep = useCallback(() => {
    if (!!onComplete) {
      onComplete(id, step);
    }
  }, [id, step, onComplete]);

  return (
    <div
      className={classNames(
        "rounded-tl-br-sm transition-short-in-out border relative",
        "has-[:focus-visible]:border-dark-blue",
        {
          "bg-neutral-0": !isComplete && isOpen,
          "bg-turquoise-200": isComplete && !isPreselected,
          "bg-neutral-100 border-neutral-100": !isComplete && !isOpen && !isPreselected,
          "pb-0": isOpen,
          "pb-sm md:pb-0": !isOpen,
          "border-neutral-300": isOpen || isComplete || isPreselected
        }
      )}
      id={id}
      data-online-booking-step={step}
      data-online-booking-step-name={ProvetDialogStep[step]}
      data-online-booking-step-completed={isComplete ? "true" : "false"}
      data-online-booking-step-preselected={isPreselected ? "true" : "false"}
      data-online-booking-step-open={isOpen ? "true" : "false"}
      {...(selectedValue && { "data-online-booking-selected-value": selectedValue })}
    >
      <div
        aria-expanded={isOpen}
        className="flex w-full p-md md:p-lg text-left bg-transparent outline-none"
      >
        <header className="flex flex-col gap-lg w-full">
          <div className="flex flex-col gap-xs">
            <div className="flex flex-row">
              <div className="flex flex-col gap-xs">
                {!!subtitle && subtitle.length > 0 && (
                  <div
                    className={classNames(
                      "text-distinct-xs font-distinct uppercase md:text-distinct-sm",
                      {
                        "text-t-neutral-800": isComplete && !isPreselected,
                        "text-dark-blue": !isComplete || isPreselected,
                        "text-t-neutral-400": !isComplete && !isOpen && !isPreselected
                      }
                    )}
                  >
                    {subtitle}
                  </div>
                )}

                {!!title && title.length > 0 && (
                  <h3
                    className={classNames("text-h3 font-guardian font-t-semi-bold", {
                      "text-dark-blue": !(!isComplete && !isOpen && !isPreselected),
                      "text-t-neutral-400": !isComplete && !isOpen && !isPreselected
                    })}
                  >
                    {title}
                  </h3>
                )}
              </div>

              <div
                className={classNames(
                  "flex flex-wrap content-center justify-center w-[3rem] h-[3rem]",
                  "p-xs ml-auto rounded-tl-br-sm transition-short-out relative",
                  {
                    "bg-turquoise-400": isComplete && !isPreselected,
                    "bg-neutral-50": !isComplete || isPreselected
                  }
                )}
              >
                {!!icon && icon.length > 0 ? (
                  <div className="flex p-sm">
                    <Svg
                      href={icon}
                      additionalClass={classNames({
                        "text-dark-blue": !(!isComplete && !isOpen && !isPreselected),
                        "text-t-neutral-400": !isComplete && !isOpen && !isPreselected
                      })}
                    />
                  </div>
                ) : (
                  <div
                    className={classNames(
                      "text-left font-guardian text-t-md font-t-semi-bold md:text-t-xl",
                      {
                        "text-dark-blue": !(!isComplete && !isOpen && !isPreselected),
                        "text-t-neutral-400": !isComplete && !isOpen && !isPreselected
                      }
                    )}
                  >
                    {step}
                  </div>
                )}

                {isComplete && !isPreselected && (
                  <Svg
                    href="/svg/system/check.svg"
                    additionalClass="absolute p-xxs bg-success-200 [&_svg]:fill-white rounded-tl-br-xs translate-y-full translate-x-full"
                  />
                )}
              </div>
            </div>
          </div>

          {(isComplete || isPreselected) &&
            !!stepCompleteMessageText &&
            (typeof stepCompleteMessageText === "string"
              ? stepCompleteMessageText.length > 0
              : true) && (
              <div className="flex flex-row items-center">
                <Svg
                  href="/svg/system/check.svg"
                  size={SvgSize.MD}
                  additionalClass={classNames("mr-xs rounded-full", {
                    "bg-turquoise-400 text-dark-blue": !isPreselected,
                    "bg-neutral-100 text-t-neutral-800": isPreselected
                  })}
                />
                {typeof stepCompleteMessageText === "string" &&
                stepCompleteMessageTextContainsHtml ? (
                  <span
                    className="inline-flex items-center gap-xs"
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(stepCompleteMessageText)
                    }}
                  ></span>
                ) : (
                  <span
                    className={classNames("inline-flex items-center gap-xs", {
                      "text-dark-blue": !isPreselected,
                      "text-t-neutral-800": isPreselected
                    })}
                  >
                    {stepCompleteMessageText}
                  </span>
                )}
              </div>
            )}

          {isComplete && !!editButtonText && !isPreselected && (
            <Button
              onClick={editStep}
              size={ButtonSize.LG}
              color={ButtonColor.Turquoise}
              additionalClass="w-fit"
              data-online-booking-action="edit"
            >
              {editButtonText}
            </Button>
          )}
          {isPreselected && (
            <div className="flex justify-between">
              <Button
                onClick={confirmStep}
                size={ButtonSize.LG}
                color={ButtonColor.Green}
                additionalClass="w-fit"
                data-online-booking-action="confirm"
              >
                {confirmButtonText}
              </Button>
              <Button
                onClick={editStep}
                size={ButtonSize.LG}
                color={ButtonColor.Neutral}
                additionalClass="w-fit"
                data-online-booking-action="change"
              >
                {changeButtonText}
              </Button>
            </div>
          )}
        </header>
      </div>

      <Collapsible isOpen={isOpen}>
        <div
          className="px-md pb-md md:px-lg md:pb-lg"
          {...{ inert: !isOpen ? "inert" : undefined }}
        >
          {children}

          {nextButtonActivated && (
            <Button
              additionalClass="w-full md:w-auto"
              onClick={completeStep}
              disabled={isComplete}
              size={ButtonSize.LG}
              data-online-booking-action="next"
            >
              {nextButtonText}
            </Button>
          )}
        </div>
      </Collapsible>

      {isLoading && isOpen && (
        <div className="absolute inset-0 bg-white/80 z-50 flex items-center justify-center rounded-tl-br-sm">
          <LoadingStatus loading={true} message="Loading" showVisual={true}>
            <Spinner visible={true} />
          </LoadingStatus>
        </div>
      )}
    </div>
  );
};
